/**
 * Balance Sheet — Assets | Liabilities + Equity, two-column layout, with a
 * verification banner confirming Assets = Liabilities + Equity.
 *
 * Data is derived from real entity data via usePersistentMockState:
 *   - accounts       → chart of accounts with types and balances
 *   - journalEntries → filter posted entries up to as-of date
 *   - journalLines   → compute account balances from posted lines
 *   - openingBalanceConfig → status hint for opening balance display
 */

import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle, AlertTriangle } from "lucide-react";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import { Input, KpiCard, Label, cn } from "@bizop/ui-kit";

import { PageHeader } from "../../../../components/PageHeader";
import { usePersistentMockState } from "../../../../lib/usePersistentMockState";
import {
  mockAccounts,
  mockJournalEntries,
  mockJournalLines,
  mockOpeningBalanceConfig,
  MOCK_NOW,
  type MockAccount,
  type MockJournalEntry,
  type MockJournalLine,
  type MockOpeningBalanceConfig,
  type AccountReportLine,
  type BalanceSheetShape,
} from "../../../../lib/glMock";
import { ExportButton } from "../../../../components/export";
import {
  downloadCsv,
  exportFilename,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../../lib/export";

export const Route = createFileRoute(
  "/_app/finance/reports/balance-sheet",
)({
  component: BalanceSheetPage,
});

/* ── Pure derive function ─────────────────────────────────────── */

function deriveBalanceSheet(
  asOfDate: number,
  accounts: MockAccount[],
  entries: MockJournalEntry[],
  lines: MockJournalLine[],
): BalanceSheetShape {
  // Build a set of posted entry IDs up to asOfDate
  const postedEntryIds = new Set<string>();
  for (const e of entries) {
    if (e.status === "posted" && e.entryDate <= asOfDate) {
      postedEntryIds.add(e._id);
    }
  }

  // Compute net (debit - credit) per account from posted lines
  const netByAccount = new Map<string, bigint>();
  for (const l of lines) {
    if (!postedEntryIds.has(l.journalEntryId)) continue;
    if (l.entryDate > asOfDate) continue;
    const prev = netByAccount.get(l.accountId) ?? 0n;
    netByAccount.set(l.accountId, prev + l.debitInBaseCents - l.creditInBaseCents);
  }

  const assets: AccountReportLine[] = [];
  const liabilities: AccountReportLine[] = [];
  const equity: AccountReportLine[] = [];

  // Revenue and expense totals for current earnings
  let revenueTotal = 0n;
  let expenseTotal = 0n;

  for (const acc of accounts) {
    if (acc._id === "acc_1500") continue; // skip parent placeholder

    const net = netByAccount.get(acc._id) ?? 0n;
    const debitNormal = acc.normalBalance === "debit";
    // Convert net to natural-side balance (positive = normal side)
    const balance = debitNormal ? net : -net;

    if (acc.accountType === "revenue") {
      revenueTotal += balance;
      continue; // revenue/expense don't appear directly on BS
    }
    if (acc.accountType === "expense") {
      expenseTotal += balance;
      continue;
    }

    if (balance === 0n && acc.accountType !== "equity") continue;

    const line: AccountReportLine = {
      accountId: acc._id,
      code: acc.code,
      name: acc.name,
      amountInBaseCents: balance,
    };

    if (acc.accountType === "asset") assets.push(line);
    else if (acc.accountType === "liability") liabilities.push(line);
    else if (acc.accountType === "equity") equity.push(line);
  }

  // Add current earnings to equity
  const earnings = revenueTotal - expenseTotal;
  if (earnings !== 0n) {
    equity.push({
      accountId: "current_earnings",
      code: "—",
      name: "Current Earnings",
      amountInBaseCents: earnings,
    });
  }

  assets.sort((a, b) => a.code.localeCompare(b.code));
  liabilities.sort((a, b) => a.code.localeCompare(b.code));
  equity.sort((a, b) => a.code.localeCompare(b.code));

  const totalAssets = assets.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const totalLiabilities = liabilities.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const totalEquity = equity.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const liabilitiesPlusEquity = totalLiabilities + totalEquity;

  return {
    asOfDate,
    assets: { accounts: assets, totalInBaseCents: totalAssets },
    liabilities: { accounts: liabilities, totalInBaseCents: totalLiabilities },
    equity: { accounts: equity, totalInBaseCents: totalEquity },
    verification: {
      liabilitiesPlusEquityInBaseCents: liabilitiesPlusEquity,
      deltaInBaseCents: totalAssets - liabilitiesPlusEquity,
      isBalanced: totalAssets === liabilitiesPlusEquity,
    },
  };
}

/* ── Component ────────────────────────────────────────────────── */

function BalanceSheetPage() {
  const [asOf, setAsOf] = useState<string>(
    new Date(MOCK_NOW).toISOString().slice(0, 10),
  );
  const asOfMs = new Date(asOf).getTime();

  const [accounts] = usePersistentMockState<MockAccount[]>("accounts", mockAccounts);
  const [entries] = usePersistentMockState<MockJournalEntry[]>("journalEntries", mockJournalEntries);
  const [lines] = usePersistentMockState<MockJournalLine[]>("journalLines", mockJournalLines);
  const [obConfig] = usePersistentMockState<MockOpeningBalanceConfig>(
    "openingBalanceConfig",
    mockOpeningBalanceConfig,
  );

  const report = useMemo(
    () => deriveBalanceSheet(asOfMs, accounts, entries, lines),
    [asOfMs, accounts, entries, lines],
  );
  const printRef = useRef<HTMLDivElement>(null);

  function handleCsv(extension: "csv" | "xls") {
    type BsRow = {
      section: string;
      code: string;
      name: string;
      balanceCents: bigint;
      currency: string;
    };
    const rows: BsRow[] = [];
    function pushBlock(label: string, accts: AccountReportLine[], total: bigint) {
      for (const a of accts) {
        rows.push({
          section: label,
          code: a.code,
          name: a.name,
          balanceCents: a.amountInBaseCents,
          currency: "BDT",
        });
      }
      rows.push({
        section: label,
        code: "",
        name: `Total ${label}`,
        balanceCents: total,
        currency: "BDT",
      });
    }
    pushBlock("Assets", report.assets.accounts, report.assets.totalInBaseCents);
    pushBlock(
      "Liabilities",
      report.liabilities.accounts,
      report.liabilities.totalInBaseCents,
    );
    pushBlock("Equity", report.equity.accounts, report.equity.totalInBaseCents);

    const columns: ExportColumn<BsRow>[] = [
      { key: "section", label: "Section" },
      { key: "code", label: "Account code" },
      { key: "name", label: "Name" },
      { key: "balanceCents", label: "Balance" },
      { key: "currency", label: "Currency" },
    ];
    downloadCsv(
      exportFilename("balance-sheet", extension),
      toCsv(rows, columns),
    );
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: "Balance Sheet",
      subtitle: `As of ${formatDate(asOfMs, { style: "medium" })}`,
    });
  }

  // Opening-balance hint + draft-mode note for OBE.
  const openingPosted =
    obConfig.status === "posted" || obConfig.status === "closed";
  const openingDraft = obConfig.status === "draft";
  const openingHint = openingPosted
    ? `Includes opening balance entry posted on ${formatDate(obConfig.asOfDate, { style: "medium" })}.`
    : null;

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      <PageHeader
        title="Balance Sheet"
        description={`Position as of ${formatDate(asOfMs, { style: "medium" })}.${openingHint ? ` ${openingHint}` : ""}`}
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
          />
        }
      />

      {openingDraft ? (
        <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3 text-[12px] text-(--text-secondary)">
          Opening balance is still in <strong>draft</strong> — the
          {" "}
          <span className="font-mono">3999 — Opening Balance Equity</span>{" "}
          line below holds pending opening balances. It will be reclassified
          to retained earnings when the workflow is closed.
        </div>
      ) : null}

      <div className="flex items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="bs-asof">As of</Label>
          <Input
            id="bs-asof"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="w-44"
          />
        </div>
      </div>

      <section
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        aria-label="Balance sheet KPIs"
      >
        <KpiCard
          caption="Total assets"
          value={formatMoneySmart(report.assets.totalInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={`${report.assets.accounts.length} accounts`}
        />
        <KpiCard
          caption="Total liabilities"
          value={formatMoneySmart(report.liabilities.totalInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={`${report.liabilities.accounts.length} accounts`}
        />
        <KpiCard
          caption="Total equity"
          value={formatMoneySmart(report.equity.totalInBaseCents, "BDT", {
            locale: "en-BD",
          })}
          hint={`${report.equity.accounts.length} accounts`}
        />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 print-two-col">
        <Column
          title="Assets"
          accounts={report.assets.accounts}
          total={report.assets.totalInBaseCents}
          tone="info"
        />
        <div className="flex flex-col gap-4">
          <Column
            title="Liabilities"
            accounts={report.liabilities.accounts}
            total={report.liabilities.totalInBaseCents}
            tone="warning"
          />
          <Column
            title="Equity"
            accounts={report.equity.accounts}
            total={report.equity.totalInBaseCents}
            tone="neutral"
          />
        </div>
      </div>

      <div
        className={cn(
          "rounded-md border p-3 text-[13px] flex items-center gap-3",
          report.verification.isBalanced
            ? "border-(--success) bg-(--bg-canvas) text-(--success)"
            : "border-(--danger) bg-(--bg-canvas) text-(--danger)",
        )}
        role={report.verification.isBalanced ? "status" : "alert"}
      >
        {report.verification.isBalanced ? (
          <CheckCircle className="h-4 w-4 shrink-0" aria-hidden />
        ) : (
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        )}
        <div className="font-mono tabular-nums">
          Assets{" "}
          <strong>
            {formatMoney(report.assets.totalInBaseCents, "BDT", {
              locale: "en-BD",
            })}
          </strong>{" "}
          ={" "}
          {report.verification.isBalanced ? null : (
            <>
              ≠ Liabilities + Equity{" "}
              <strong>
                {formatMoney(
                  report.verification.liabilitiesPlusEquityInBaseCents,
                  "BDT",
                  { locale: "en-BD" },
                )}
              </strong>{" "}
              <span className="text-(--danger)">
                (Δ{" "}
                {formatMoney(report.verification.deltaInBaseCents, "BDT", {
                  locale: "en-BD",
                })}
                )
              </span>
            </>
          )}
          {report.verification.isBalanced ? (
            <>
              Liabilities + Equity{" "}
              <strong>
                {formatMoney(
                  report.verification.liabilitiesPlusEquityInBaseCents,
                  "BDT",
                  { locale: "en-BD" },
                )}
              </strong>{" "}
              ✓
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Column({
  title,
  accounts,
  total,
  tone,
}: {
  title: string;
  accounts: AccountReportLine[];
  total: bigint;
  tone: "info" | "warning" | "neutral";
}) {
  return (
    <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) overflow-hidden">
      <div
        className={cn(
          "px-3 py-2 border-b border-(--border-subtle) text-[12px] uppercase tracking-wider font-semibold",
          tone === "info" && "text-(--text-primary)",
          tone === "warning" && "text-(--text-primary)",
          tone === "neutral" && "text-(--text-primary)",
        )}
      >
        {title}
      </div>
      <table className="w-full text-[13px]">
        <tbody>
          {accounts.length === 0 ? (
            <tr>
              <td
                className="px-3 py-3 text-center text-(--text-tertiary)"
                colSpan={2}
              >
                No balances
              </td>
            </tr>
          ) : null}
          {accounts.map((a) => (
            <tr
              key={a.accountId}
              className="border-t border-(--border-subtle) hover:bg-(--bg-hover)"
            >
              <td className="px-3 py-2">
                <span className="font-mono mr-2 text-[12px] text-(--text-secondary)">
                  {a.code}
                </span>
                <span className="text-[13px] text-(--text-primary)">
                  {a.name}
                </span>
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-(--text-primary)">
                {formatMoney(a.amountInBaseCents, "BDT", { locale: "en-BD" })}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-(--border-subtle) bg-(--bg-surface)">
            <td className="px-3 py-2 font-semibold text-(--text-primary)">
              Total {title.toLowerCase()}
            </td>
            <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-(--text-primary)">
              {formatMoney(total, "BDT", { locale: "en-BD" })}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
